import {
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  type InputProps,
  Stack,
} from "@chakra-ui/react";
import { type FC, type ReactNode, forwardRef, useState } from "react";

export interface MaskedInputProps extends Omit<InputProps, "type"> {
  name: string;
  value?: string;
  label: string;
  error?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  helperText?: ReactNode;
  showLabel?: string;
  fieldType?: "SSN" | "TIN" | "EIN" | "SSN/TIN" | "custom";
}

export const MaskedInput: FC<MaskedInputProps> = forwardRef(
  (
    {
      name,
      label,
      value,
      isDisabled = false,
      isRequired = false,
      isInvalid = false,
      error,
      helperText,
      showLabel,
      fieldType = "custom",
      mb = 5,
      color,
      ...rest
    },
    ref,
  ) => {
    const [showValue, setShowValue] = useState(false);

    // Generate default show label based on fieldType
    const defaultShowLabel = showLabel || (fieldType !== "custom" ? `Show ${fieldType}` : "Show value");

    return (
      <FormControl isDisabled={isDisabled} isRequired={isRequired} isInvalid={isInvalid} mb={mb}>
        <FormLabel fontSize="md">{label}</FormLabel>
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing={{ base: 2, md: 4 }}
          align={{ base: "stretch", md: "flex-start" }}>
          <InputGroup flex={1}>
            <Input
              ref={ref}
              type="text"
              name={name}
              value={value ?? undefined}
              bg="white"
              borderWidth="1px"
              borderRadius="5px"
              pt="10px"
              pb="10px"
              m="0"
              inputMode="text"
              role="textbox"
              aria-autocomplete="none"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              className="fvMask"
              spellCheck={false}
              autoComplete="off"
              sx={{
                ...(showValue
                  ? {}
                  : {
                      fontFamily: "password, monospace",
                      WebkitTextSecurity: "disc",
                      textSecurity: "disc",
                    }),
              }}
              data-show={showValue ? "true" : "false"}
              {...rest}
            />
          </InputGroup>
          <Checkbox
            mt={{ base: 0, md: 2 }}
            color={color}
            isChecked={showValue}
            isRequired={false}
            size="md"
            onChange={() => setShowValue((prev) => !prev)}>
            {defaultShowLabel}
          </Checkbox>
        </Stack>
        <FormHelperText>{helperText}</FormHelperText>
        <FormErrorMessage>{error}</FormErrorMessage>
      </FormControl>
    );
  },
);
